/* eslint-disable no-console */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { env } from 'config/env';
import {
    getCookieJSON,
    PkAdminCookie,
    PkBaseAdminCookie,
    getBaseAdmin,
    setBaseAdmin,
    clearBaseAdmin,
} from 'util/cookieUtils';
import {
    AuthFunctions,
    AuthState,
    UserAuthDetails,
    AuthData,
} from 'entities/authentication';
import { AuthenticateResponse } from 'api/interface/authenticate';
import { apiBase, setApiAccessToken, markLastAuthAt } from 'api/api';
import { clearSessionExpiredFromUrl } from 'api/sessionExpired';
import { authApi } from 'api/auth';
import i18next from 'i18next';
import {
    EventType,
    AuthenticationResult,
    InteractionRequiredAuthError,
    AccountInfo,
    EventMessage,
} from '@azure/msal-browser';
import { msalConfig } from 'msalConfig';
import { safeLocalStorage } from 'util/safeLocalStorage';

const INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const INACTIVITY_STORAGE_KEY = 'pk_last_activity';

const resetActivityTimestamp = (): void => {
    safeLocalStorage.setItem(INACTIVITY_STORAGE_KEY, String(Date.now()));
};

const isActivityStale = (): boolean => {
    const stored = safeLocalStorage.getItem(INACTIVITY_STORAGE_KEY);
    if (!stored) {
        return false;
    }
    return Date.now() - Number(stored) >= INACTIVITY_TIMEOUT_MS;
};

const redirectToPostLoginHome = (
    permissions: Record<string, number> | undefined
): void => {
    const isReceptionUser = Number(permissions?.Reception) > 0;
    if (isReceptionUser) {
        return;
    }

    const path = window.location.pathname;
    if (path === '/' || path === '/login' || path === '/auth-redirect') {
        window.history.replaceState({}, '', '/dashboard');
    }
};

const AuthContext = React.createContext({});
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const useAuth = () => React.useContext(AuthContext) as AuthData;

const computePermissions = (
    permissions: Record<string, number> | undefined
) => {
    const userPermissions: Record<
        string,
        {
            canRead: boolean;
            canWrite: boolean;
            canDelete: boolean;
        }
    > = {};

    if (!permissions) return userPermissions;
    if (!Object.keys(permissions)?.length) return userPermissions;

    Object.entries(permissions)?.forEach(([module_name, value]) => {
        const numeric = Number(value);
        userPermissions[module_name] = {
            // eslint-disable-next-line no-bitwise
            canRead: (numeric & 1) !== 0, // Read = 1
            // eslint-disable-next-line no-bitwise
            canWrite: (numeric & 2) !== 0, // Write = 2
            // eslint-disable-next-line no-bitwise
            canDelete: (numeric & 4) !== 0, // Delete = 4
        };
    });

    return userPermissions;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
const AuthProvider = (props: any): JSX.Element => {
    const [state, setState] = useState<AuthState>({
        name: '',
        accessToken: '',
        isAuthenticated: false,
        id: 0,
        organisationID: 0,
        organisation_logo: '',
        loading: true,
        authHydrated: false,
        error: null,
        email: '',
        backoffice_organisation: false,
        backoffice_organisation_id: 0,
        belongs_to_backoffice: null,
        user_organisation_roles: {
            organisation_id: 0,
            role_id: 0,
        },
        permissions: {},
        language: 'dansk',
        tokenExpiry: 0,
        entra_id_enabled: env.loginEntraIdEnabled,
        email_pass_enabled: env.loginEmailPasswordEnabled,
        entraPayload: undefined,
    });

    const userPermissions = useMemo(
        () => computePermissions(state.permissions),
        [state.permissions]
    );

    const setLanguage = (language: string) => {
        safeLocalStorage.setItem('language', language);

        if (language === state.language) return;
        setState((state) => ({ ...state, language }));
        i18next.changeLanguage(language);
    };

    const { t } = useTranslation();

    const setUserPermissions = React.useCallback(
        (
            res: AuthenticateResponse,
            authMethod?: 'email_password' | 'entra'
        ): boolean => {
            if (!res.authenticated) {
                const errorMessage = res.error
                    ? res.error
                    : t('authenticationFailed');

                setApiAccessToken(null);
                setState((prev) => ({
                    ...prev,
                    name: '',
                    accessToken: '',
                    isAuthenticated: false,
                    id: 0,
                    organisationID: 0,
                    backoffice_organisation_id: 0,
                    organisation_logo: '',
                    email: '',
                    loading: false,
                    authHydrated: true,
                    error: errorMessage,
                    tokenExpiry: 0,
                }));
                return false;
            }

            clearBaseAdmin();
            resetActivityTimestamp();
            markLastAuthAt();
            setState((prev) => {
                Cookies.set(
                    'pk-admin',
                    JSON.stringify({
                        userID: res.user_id,
                        token: res.token,
                        organisationID: res.organisation_id,
                        email: res.email,
                        username: res.username,
                        permissions: res.role_permissions,
                        language: prev.language,
                        backoffice_organisation: res.backoffice_organisation,
                        backoffice_organisation_id:
                            res.backoffice_organisation_id,
                        belongs_to_backoffice: res.belongs_to_backoffice,
                        organisation_logo: res.organisation_logo,
                        tokenExpiry: res.tokenExpiry,
                        authMethod,
                    })
                );

                return {
                    ...prev,
                    isAuthenticated: res.authenticated,
                    accessToken: res.token,
                    id: res.user_id,
                    organisationID: res.organisation_id,
                    backoffice_organisation: res.backoffice_organisation,
                    backoffice_organisation_id: res.backoffice_organisation_id,
                    belongs_to_backoffice: res.belongs_to_backoffice,
                    name: res.username,
                    email: res.email,
                    permissions: res.role_permissions,
                    loading: false,
                    authHydrated: true,
                    error: null,
                    organisation_logo: res.organisation_logo,
                    tokenExpiry: res.tokenExpiry,
                    entraPayload: undefined,
                    authMethod,
                };
            });

            setApiAccessToken(res.token);
            clearSessionExpiredFromUrl();
            redirectToPostLoginHome(res.role_permissions);

            return true;
        },
        [t]
    );

    const getBackendAccessToken = React.useCallback(
        async (tokenResponse: AuthenticationResult): Promise<boolean> => {
            // Do something with the tokenResponse
            const res: AuthenticateResponse =
                await authApi.authenticateEntraId(tokenResponse);
            if (!res.authenticated) {
                res.error = res.error ? res.error : 'The Entra ID login failed';
            }
            const status = await setUserPermissions(res, 'entra');
            return status;
        },
        [setUserPermissions]
    );

    const entraInteractiveLogin = React.useCallback(async (): Promise<void> => {
        await props.msalInstance.loginRedirect({
            scopes: [env.entraScope || 'user.read'],
            authority: msalConfig.auth.authority,
        });
    }, [props.msalInstance]);

    const getSilentTokenForAccount = React.useCallback(
        async (selectedAccount: AccountInfo): Promise<AuthenticationResult> => {
            let tokenResponse = {} as AuthenticationResult;
            const tokenRequest = {
                account: selectedAccount,
                scopes: [env.entraScope || 'user.read'],
                authority: msalConfig.auth.authority,
                forceRefresh: false,
            };
            try {
                tokenResponse =
                    await props.msalInstance.acquireTokenSilent(tokenRequest);
                // Set user token
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    await props.msalInstance.acquireTokenRedirect(tokenRequest);
                    return tokenResponse;
                }
                throw error;
            }
            if (tokenResponse && tokenResponse.accessToken) {
                await getBackendAccessToken(tokenResponse);
            }
            return tokenResponse;
        },
        [props.msalInstance, getBackendAccessToken]
    );

    const entraLogin = React.useCallback(async () => {
        if (!props.msalInstance) {
            return;
        }
        setState((prev) => ({ ...prev, loading: true }));
        await entraInteractiveLogin();
    }, [props.msalInstance, entraInteractiveLogin]);

    const entraSSOLoginRef = React.useRef(false);

    const entraSSOLogin = React.useCallback(async (): Promise<AccountInfo> => {
        let selectedAccount = {} as AccountInfo;
        if (entraSSOLoginRef.current) return selectedAccount;
        entraSSOLoginRef.current = true;
        try {
            const loginRequest = {
                scopes: [env.entraScope || 'user.read'],
                authority: msalConfig.auth.authority,
            };
            const entraRes = await props.msalInstance.ssoSilent(loginRequest);
            if (entraRes.account) {
                selectedAccount = entraRes.account;
                props.msalInstance.setActiveAccount(selectedAccount);
            }
        } catch (error) {
            // Don't redirect automatically. Let user decide.
        } finally {
            entraSSOLoginRef.current = false;
        }
        return selectedAccount;
    }, [props.msalInstance]);

    const checkActiveAccount =
        React.useCallback(async (): Promise<AccountInfo> => {
            let selectedAccount = {} as AccountInfo;
            // Set active account if available
            try {
                selectedAccount = props.msalInstance.getActiveAccount();
            } catch (error) {
                console.log('Error getting active account:', error);
            } finally {
                if (
                    !selectedAccount &&
                    props.msalInstance.getAllAccounts().length > 0
                ) {
                    selectedAccount = props.msalInstance
                        .getAllAccounts()
                        .find(
                            (account: AccountInfo) =>
                                account.tenantId === env.tenantId
                        );
                    if (selectedAccount) {
                        props.msalInstance.setActiveAccount(selectedAccount);
                    }
                }
            }
            if (!selectedAccount) {
                // fallback to interactive login if silent fails
                selectedAccount = await entraSSOLogin();
            }
            return selectedAccount;
        }, [props.msalInstance, entraSSOLogin]);

    useEffect(() => {
        setApiAccessToken(state.accessToken || null);
    }, [state.accessToken]);

    const logout = React.useCallback(async () => {
        // remove the pk-admin cookie
        try {
            await authApi.logout();
        } catch {
            // intentionally left blank
        }

        safeLocalStorage.removeItem(INACTIVITY_STORAGE_KEY);
        Cookies.remove('pk-admin');
        Cookies.set('pk-admin', JSON.stringify({ language: state.language }));
        clearBaseAdmin();
        Cookies.remove('pk-base-admin');
        setApiAccessToken(null);
        delete apiBase.defaults.headers.common?.Token;

        setState((prev) => ({
            ...prev,
            name: '',
            accessToken: '',
            isAuthenticated: false,
            id: 0,
            organisationID: 0,
            organisation_logo: '',
            loading: false,
            authHydrated: true,
            error: null,
            email: '',
            backoffice_organisation: false,
            backoffice_organisation_id: 0,
            belongs_to_backoffice: null,
            user_organisation_roles: {
                organisation_id: 0,
                role_id: 0,
            },
            permissions: {},
            tokenExpiry: 0,
            language: prev.language,
        }));

        if (env.loginEntraIdEnabled && state.authMethod === 'entra') {
            props.msalInstance.logoutPopup({
                postLogoutRedirectUri: '/',
            });
        } else {
            window.location.href = '/';
        }
    }, [props.msalInstance, state.authMethod, state.language]);

    // Stable ref so the inactivity timer always calls the latest logout without
    // needing it as an effect dependency (logout re-creates on every state change).
    const logoutRef = React.useRef(logout);
    useEffect(() => {
        logoutRef.current = logout;
    }, [logout]);

    const refreshTokeAndSet = async (): Promise<string | undefined> => {
        try {
            const refreshed = await authApi.refreshToken();
            if (refreshed?.token) {
                const currentCookie =
                    getCookieJSON<PkAdminCookie>('pk-admin') || {};
                Cookies.set(
                    'pk-admin',
                    JSON.stringify({
                        ...currentCookie,
                        token: refreshed.token,
                        tokenExpiry: refreshed.tokenExpiry,
                    })
                );
                setApiAccessToken(refreshed.token);
                setState((prev) => ({
                    ...prev,
                    accessToken: refreshed.token,
                    tokenExpiry: refreshed.tokenExpiry,
                }));
                return refreshed.token;
            }
        } catch {
            // intentionally left blank
        }
        return undefined;
    };

    useEffect(() => {
        let cancelled = false;

        const language = safeLocalStorage.getItem('language') || 'dansk';
        setLanguage(language);

        const completeHydration = () => {
            if (!cancelled) {
                setState((s) => ({
                    ...s,
                    loading: false,
                    authHydrated: true,
                }));
            }
        };

        let tknDetails = getCookieJSON<PkAdminCookie>('pk-admin');

        const legacyBase = getCookieJSON<PkBaseAdminCookie>('pk-base-admin');
        if (legacyBase) {
            if (!getBaseAdmin()) {
                setBaseAdmin(legacyBase);
            }
            Cookies.remove('pk-base-admin');
        }

        if (tknDetails?.token && !tknDetails.authMethod) {
            const migrated: PkAdminCookie = {
                ...tknDetails,
                authMethod: 'email_password',
            };
            Cookies.set('pk-admin', JSON.stringify(migrated));
            tknDetails = migrated;
        }

        const hasValidCookie = !!(
            tknDetails?.token && tknDetails.token.length > 0
        );
        const isEntraEnv = env.loginEntraIdEnabled;

        const applyCookieSession = (details: PkAdminCookie, token: string) => {
            setApiAccessToken(token);
            setState((s) => ({
                ...s,
                name: details.username ?? '',
                accessToken: token,
                isAuthenticated: true,
                id: details.userID ?? 0,
                organisationID: details.organisationID ?? 0,
                email: details.email ?? '',
                backoffice_organisation:
                    details.backoffice_organisation ?? false,
                backoffice_organisation_id:
                    details.backoffice_organisation_id ?? 0,
                belongs_to_backoffice: details.belongs_to_backoffice,
                permissions: details.permissions ?? {},
                language,
                organisation_logo: details.organisation_logo ?? '',
                tokenExpiry: details.tokenExpiry ?? 0,
                authMethod: details.authMethod,
            }));
            redirectToPostLoginHome(details.permissions);
            completeHydration();
        };

        const restoreFromCookie = () => {
            const cookieDetails = tknDetails;
            if (!cookieDetails?.token) {
                completeHydration();
                return;
            }

            const nowSeconds = Math.floor(Date.now() / 1000);
            const storedExpiry = cookieDetails.tokenExpiry ?? 0;
            const isExpired = storedExpiry > 0 && nowSeconds >= storedExpiry;
            const isReception = (cookieDetails.permissions?.Reception ?? 0) > 0;

            if (isExpired && isReception) {
                setApiAccessToken(cookieDetails.token);
                authApi
                    .refreshToken()
                    .then((refreshed) => {
                        if (cancelled) return;
                        if (refreshed?.token) {
                            const currentCookie =
                                getCookieJSON<PkAdminCookie>('pk-admin') || {};
                            Cookies.set(
                                'pk-admin',
                                JSON.stringify({
                                    ...currentCookie,
                                    token: refreshed.token,
                                    tokenExpiry: refreshed.tokenExpiry,
                                })
                            );
                            applyCookieSession(cookieDetails, refreshed.token);
                        } else {
                            logoutRef.current();
                            completeHydration();
                        }
                    })
                    .catch(() => {
                        if (!cancelled) {
                            logoutRef.current();
                            completeHydration();
                        }
                    });
                return;
            }

            if (isExpired) {
                logoutRef.current();
                completeHydration();
                return;
            }

            const isReceptionUser =
                (cookieDetails.permissions?.Reception ?? 0) > 0;
            if (!isReceptionUser && isActivityStale()) {
                logoutRef.current();
                completeHydration();
                return;
            }

            applyCookieSession(cookieDetails, cookieDetails.token);
        };

        if (hasValidCookie) {
            restoreFromCookie();
            return () => {
                cancelled = true;
            };
        }

        if (isEntraEnv && props.msalInstance) {
            let entraHydrationDone = false;

            const finishEntraHydration = () => {
                if (!cancelled && !entraHydrationDone) {
                    entraHydrationDone = true;
                    completeHydration();
                }
            };

            props.msalInstance
                .initialize()
                .then(() => {
                    props.msalInstance.addEventCallback(
                        (event: EventMessage) => {
                            if (
                                (event.eventType === EventType.LOGIN_SUCCESS ||
                                    event.eventType ===
                                        EventType.ACQUIRE_TOKEN_SUCCESS) &&
                                event.payload &&
                                (event.payload as AuthenticationResult).account
                            ) {
                                props.msalInstance.setActiveAccount(
                                    (event.payload as AuthenticationResult)
                                        .account
                                );
                            }
                        }
                    );

                    return props.msalInstance
                        .handleRedirectPromise()
                        .then(
                            async (
                                redirectResponse: AuthenticationResult | null
                            ) => {
                                if (cancelled) return;
                                if (
                                    redirectResponse &&
                                    redirectResponse.account
                                ) {
                                    props.msalInstance.setActiveAccount(
                                        redirectResponse.account
                                    );
                                    await getBackendAccessToken(
                                        redirectResponse
                                    );
                                    return;
                                }
                                const entraAccount = await checkActiveAccount();
                                if (entraAccount?.username) {
                                    await getSilentTokenForAccount(
                                        entraAccount
                                    );
                                }
                            }
                        );
                })
                .catch((error: Error) => {
                    console.error('Error handling redirect:', error);
                })
                .finally(() => {
                    finishEntraHydration();
                });

            return () => {
                cancelled = true;
            };
        }

        completeHydration();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // TODO: revisited this auth flow when getting closer to prod
    const login = async (e: React.FormEvent, payload: UserAuthDetails) => {
        e.preventDefault();
        if (state.loading) return;
        setState((prev) => ({ ...prev, loading: true }));

        const res: AuthenticateResponse = await authApi.authenticate({
            userName: payload.username,
            password: payload.password,
        });

        if (!res.authenticated) {
            if (
                res.error &&
                res.error !==
                    'User account is inactive. Please contact support.'
            ) {
                res.error = 'The username or password is incorrect';
            }
        }
        setUserPermissions(res, 'email_password');
        setState((prev) => {
            if (prev.loading) {
                return { ...prev, loading: false };
            }
            return prev;
        });
    };

    const getTokenSecondsLeft = React.useCallback(
        (tokenExpiry: number): number | null => {
            const expiry =
                typeof tokenExpiry === 'number' &&
                !Number.isNaN(tokenExpiry) &&
                tokenExpiry > 0
                    ? tokenExpiry * 1000
                    : null;
            if (!expiry) return null;
            const now = Date.now();
            const secondsLeft = Math.floor((expiry - now) / 1000);
            return secondsLeft >= 0 ? secondsLeft : null;
        },
        []
    );

    useEffect(() => {
        const isBackoffice = state.backoffice_organisation === true;
        const hasReceptionAccess = Number(state.permissions?.Reception) > 0;

        if (!isBackoffice && !hasReceptionAccess) {
            return undefined;
        }
        if (!state.accessToken || !state.isAuthenticated) {
            return undefined;
        }
        const expiry = getTokenSecondsLeft(state.tokenExpiry);
        if (!expiry) return undefined;

        const refreshThreshold = 2 * 60; // 2 minutes before expiry
        const delay = expiry - refreshThreshold;
        if (delay <= 0) {
            if (env.loginEntraIdEnabled) {
                (async () => {
                    const entraAccount = await checkActiveAccount();
                    if (entraAccount?.username) {
                        await getSilentTokenForAccount(entraAccount);
                    }
                })();
            } else {
                (async () => {
                    try {
                        const refreshed = await authApi.refreshToken();
                        if (refreshed?.token) {
                            const currentCookie =
                                getCookieJSON<PkAdminCookie>('pk-admin') || {};
                            Cookies.set(
                                'pk-admin',
                                JSON.stringify({
                                    ...currentCookie,
                                    token: refreshed.token,
                                    tokenExpiry: refreshed.tokenExpiry,
                                })
                            );
                            setApiAccessToken(refreshed.token);
                            setState((prev) => ({
                                ...prev,
                                accessToken: refreshed.token,
                                tokenExpiry: refreshed.tokenExpiry,
                            }));
                        }
                    } catch {
                        // intentionally left blank
                    }
                })();
            }
            return undefined;
        }
        const timeoutID = setTimeout(async () => {
            try {
                if (env.loginEntraIdEnabled) {
                    (async () => {
                        const entraAccount = await checkActiveAccount();
                        if (entraAccount?.username) {
                            await getSilentTokenForAccount(entraAccount);
                        }
                    })();
                } else {
                    const refreshed = await authApi.refreshToken();
                    if (refreshed?.token) {
                        const currentCookie =
                            getCookieJSON<PkAdminCookie>('pk-admin') || {};
                        Cookies.set(
                            'pk-admin',
                            JSON.stringify({
                                ...currentCookie,
                                token: refreshed.token,
                                tokenExpiry: refreshed.tokenExpiry,
                            })
                        );
                        setApiAccessToken(refreshed.token);
                        setState((prev) => ({
                            ...prev,
                            accessToken: refreshed.token,
                            tokenExpiry: refreshed.tokenExpiry,
                        }));
                    }
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Token refresh failed', err);
            }
        }, delay * 1000);

        return () => clearTimeout(timeoutID);
    }, [
        state.accessToken,
        state.isAuthenticated,
        state.backoffice_organisation,
        state.backoffice_organisation_id,
        state.permissions?.Reception,
        state.tokenExpiry,
        getTokenSecondsLeft,
        checkActiveAccount,
        getSilentTokenForAccount,
    ]);

    // --- Inactivity logout (12 h) ---
    // Reception users are explicitly excluded: they must stay logged in indefinitely.
    useEffect(() => {
        const hasReceptionAccess = Number(state.permissions?.Reception) > 0;
        if (hasReceptionAccess || !state.isAuthenticated) return undefined;

        const stored = safeLocalStorage.getItem(INACTIVITY_STORAGE_KEY);
        const now = Date.now();

        if (!stored) {
            safeLocalStorage.setItem(INACTIVITY_STORAGE_KEY, String(now));
        }

        let timeoutId: ReturnType<typeof setTimeout>;

        const resetTimer = () => {
            safeLocalStorage.setItem(
                INACTIVITY_STORAGE_KEY,
                String(Date.now())
            );
            clearTimeout(timeoutId);
            timeoutId = setTimeout(
                () => logoutRef.current(),
                INACTIVITY_TIMEOUT_MS
            );
        };

        const initialDelay = stored
            ? Math.max(0, INACTIVITY_TIMEOUT_MS - (now - Number(stored)))
            : INACTIVITY_TIMEOUT_MS;

        const activityEvents = [
            'mousemove',
            'mousedown',
            'keydown',
            'touchstart',
            'scroll',
        ] as const;

        activityEvents.forEach((e) => window.addEventListener(e, resetTimer));
        timeoutId = setTimeout(() => logoutRef.current(), initialDelay);

        return () => {
            activityEvents.forEach((e) =>
                window.removeEventListener(e, resetTimer)
            );
            clearTimeout(timeoutId);
        };
    }, [state.isAuthenticated, state.permissions?.Reception]);

    const authentication: AuthFunctions = {
        login,
        logout,
        entraLogin,
    };

    return (
        <AuthContext.Provider
            value={{
                id: state.id,
                name: state.name,
                organisationID: state.organisationID,
                organisation_logo: state.organisation_logo,
                backoffice_organisation: state.backoffice_organisation,
                backoffice_organisation_id: state.backoffice_organisation_id,
                belongs_to_backoffice: state.belongs_to_backoffice,
                accessToken: state.accessToken,
                isAuthenticated: state.isAuthenticated,
                email: state.email,
                user_organisation_roles: state.user_organisation_roles,
                permissions: state.permissions,
                authentication,
                userPermissions,
                error: state.error,
                loadingState: state.loading,
                authHydrated: state.authHydrated,
                language: state.language,
                setLanguage,
                refreshTokeAndSet,
                entra_id_enabled: state.entra_id_enabled,
                email_pass_enabled: state.email_pass_enabled,
                entraPayload: state.entraPayload,
            }}
            {...props}
        />
    );
};

export default AuthProvider;
